import * as React from "react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useGetPersonDetailsQuery } from "../../store/api/genreApi";

const BIO_PREVIEW_LEN = 420;

type KnownCredit = {
  id: number;
  media: "movie" | "tv";
  title: string;
  poster_path: string | null;
  vote_average?: number;
};

type CreditRow = KnownCredit & { popularity?: number };

function buildKnownFor(data: {
  movie_credits?: { cast?: unknown[] };
  tv_credits?: { cast?: unknown[] };
}): KnownCredit[] {
  const movieCast = (data.movie_credits?.cast || []) as Array<{
    id: number;
    title?: string;
    original_title?: string;
    poster_path?: string | null;
    vote_average?: number;
    popularity?: number;
  }>;
  const tvCast = (data.tv_credits?.cast || []) as Array<{
    id: number;
    name?: string;
    original_name?: string;
    poster_path?: string | null;
    vote_average?: number;
    popularity?: number;
  }>;

  const combined: CreditRow[] = [
    ...movieCast.map((c) => ({
      id: c.id,
      media: "movie" as const,
      title: c.title || c.original_title || "Untitled",
      poster_path: c.poster_path ?? null,
      vote_average: c.vote_average,
      popularity: c.popularity,
    })),
    ...tvCast.map((c) => ({
      id: c.id,
      media: "tv" as const,
      title: c.name || c.original_name || "Untitled",
      poster_path: c.poster_path ?? null,
      vote_average: c.vote_average,
      popularity: c.popularity,
    })),
  ];

  combined.sort((a, b) => {
    const byVote = (b.vote_average || 0) - (a.vote_average || 0);
    if (byVote !== 0) {
      return byVote;
    }
    return (b.popularity || 0) - (a.popularity || 0);
  });

  const seen = new Set<string>();
  const deduped: KnownCredit[] = [];
  for (const item of combined) {
    const key = `${item.media}-${item.id}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    deduped.push({
      id: item.id,
      media: item.media,
      title: item.title,
      poster_path: item.poster_path,
      vote_average: item.vote_average,
    });
    if (deduped.length >= 12) {
      break;
    }
  }
  return deduped;
}

const PersonDetailPage: React.FC = () => {
  const { personId = "" } = useParams<{ personId: string }>();
  const [bioExpanded, setBioExpanded] = useState(false);

  const { data, isLoading, error, refetch } = useGetPersonDetailsQuery(
    personId,
    { skip: !personId },
  );

  const knownFor = useMemo(() => (data ? buildKnownFor(data) : []), [data]);

  if (!personId) {
    return (
      <section className="container page-section person-detail-page">
        <p>Missing person id.</p>
        <Link to="/people" className="btn-pill">
          Back to people
        </Link>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className="container page-section person-detail-page">
        <p className="person-detail-status">Loading…</p>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="container page-section person-detail-page">
        <p className="person-detail-error">Could not load this person.</p>
        <button type="button" className="btn-pill" onClick={() => refetch()}>
          Retry
        </button>
        <Link to="/people" className="btn-pill person-detail-back">
          Back to people
        </Link>
      </section>
    );
  }

  const bio = (data.biography || "").trim();
  const showBioToggle = bio.length > BIO_PREVIEW_LEN;
  const bioDisplay =
    bioExpanded || !showBioToggle
      ? bio
      : `${bio.slice(0, BIO_PREVIEW_LEN).trim()}…`;

  const profileSrc =
    data.profile_path && String(data.profile_path).length > 0
      ? `https://image.tmdb.org/t/p/h632${data.profile_path}`
      : "/images/no-image.jpg";

  return (
    <section className="container page-section person-detail-page">
      <div className="person-detail-toolbar">
        <Link to="/people" className="btn-pill person-detail-back">
          ← People
        </Link>
      </div>

      <div className="person-detail-header">
        <div className="person-detail-photo-wrap">
          <img
            src={profileSrc}
            alt={data.name}
            className="person-detail-photo"
            decoding="async"
          />
        </div>
        <div className="person-detail-intro">
          <h1 className="person-detail-name">{data.name}</h1>
          {data.known_for_department ? (
            <p className="person-detail-dept">{data.known_for_department}</p>
          ) : null}
          <ul className="person-detail-facts">
            {data.birthday ? (
              <li>
                <span className="text-secondary">Born:</span> {data.birthday}
                {data.place_of_birth ? ` · ${data.place_of_birth}` : ""}
              </li>
            ) : null}
            {data.homepage ? (
              <li>
                <a href={data.homepage} target="_blank" rel="noreferrer">
                  Website
                </a>
              </li>
            ) : null}
          </ul>
        </div>
      </div>

      <div className="person-detail-bio">
        <h2>Biography</h2>
        {bio ? (
          <>
            <p className="person-detail-bio-text">{bioDisplay}</p>
            {showBioToggle ? (
              <button
                type="button"
                className="person-detail-bio-toggle"
                onClick={() => setBioExpanded((e) => !e)}
              >
                {bioExpanded ? "Show less" : "Read more"}
              </button>
            ) : null}
          </>
        ) : (
          <p className="text-muted">No biography available.</p>
        )}
      </div>

      {knownFor.length > 0 ? (
        <div className="person-detail-known">
          <h2>Known for</h2>
          <div className="person-known-grid">
            {knownFor.map((item) => {
              const href =
                item.media === "movie"
                  ? `/movie-details/${item.id}`
                  : `/tv-details/${item.id}`;
              const poster =
                item.poster_path && String(item.poster_path).length > 0
                  ? `https://image.tmdb.org/t/p/w342${item.poster_path}`
                  : "/images/no-image.jpg";
              return (
                <article key={`${item.media}-${item.id}`} className="person-known-card">
                  <Link to={href} className="person-known-link">
                    <div className="person-known-poster-wrap">
                      <img
                        src={poster}
                        alt={item.title}
                        className="person-known-poster"
                        decoding="async"
                      />
                    </div>
                    <h3 className="person-known-title">{item.title}</h3>
                    {item.vote_average != null ? (
                      <p className="person-known-rating">
                        {item.vote_average.toFixed(1)} / 10
                      </p>
                    ) : null}
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default PersonDetailPage;
